import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getApiUrl } from '@/config/api';
import { authenticatedFetchWithRetry } from '@/utils/apiUtils';
import {
  TrendingUp, TrendingDown, ChevronDown, ChevronUp,
  AlertCircle, Watch, Shield, Activity,
} from 'lucide-react';
import { Minus } from 'lucide-react';
import { InfoTooltip } from '@/components/shared';
import OverlayExportWrapper from '@/components/shared/OverlayExportWrapper';
import {
  READINESS_SCORE_TOOLTIP, COMPONENT_TOOLTIPS,
  INJURY_RISK_CONFIDENCE_TOOLTIP,
  CONTEXTUAL_RECOMMENDATIONS_TOOLTIP,
  CONTEXT_HEADLINE_TOOLTIP,
  INJURY_CONTEXT_ONBOARDING_TOOLTIP,
} from '@/constants/tooltips';
import InjuryHistoryPanel from './InjuryHistoryPanel';
import {
  Chart as ChartJS,
  RadialLinearScale, CategoryScale, LinearScale,
  PointElement, LineElement, Filler,
  Tooltip as ChartTooltip, Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { InjuryRiskTimelinePoint } from '@/types';
import SignalFlowNetwork from './SignalFlowNetwork';
import SignalContributorCard from './SignalContributorCard';
import CompactTimeline from './CompactTimeline';
import type { SignalMetadataEntry, InteractionNode } from '@/types';

ChartJS.register(
  RadialLinearScale, CategoryScale, LinearScale,
  PointElement, LineElement, Filler, ChartTooltip, Legend,
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AthleteStatusCardProps {
  athleteId?: number;
  isDark: boolean;
  defaultExpanded?: boolean;
  connectedSources?: string[];
}

type ActiveTab = 'readiness' | 'injury-risk';

// Readiness types
interface ReadinessResponse {
  readiness_score: number;
  tier: string;
  confidence_adjusted_score: number;
  score_range: { lower: number; upper: number; center: number };
  components?: {
    hrv?: {
      score?: number; z_score?: number; composite_score: number;
      raw_value?: number; baseline_mean?: number; unit?: string;
      trend?: number; weekly_trend?: number | null; is_estimated?: boolean;
    };
    rhr?: number | {
      score: number; raw_value?: number; unit?: string;
      trend?: number; weekly_trend?: number | null;
    };
    sleep?: {
      final_score: number; duration_score?: number; consistency_penalty?: number;
      raw_value?: number; avg_duration?: number; unit?: string;
      trend?: number; weekly_trend?: number | null;
    };
    acwr?: {
      score: number; raw_value?: number; unit?: string;
      acute_load?: number; chronic_load?: number; zone?: string;
    } | null;
    wellness?: { composite_score: number; hooper_index?: number } | null;
  };
  device_confidence?: { overall_confidence: number; device_type: string };
  date: string;
  warnings: string[];
}

interface MetricData {
  name: string; rawValue: number; unit: string; score: number;
  trend: number; weeklyTrend?: number | null; available: boolean;
  isEstimated?: boolean;
}

// Injury risk types
interface SignalContribution {
  signal: string; raw_value: number; risk_score: number;
  weighted_contribution: number; direction: string; explanation: string;
}

interface RegionalPainSummary {
  region: string; pain_level: number; pain_type: string;
  is_new_onset: boolean; report_date: string;
  previous_injury?: { days_ago: number; severity: string; is_active: boolean };
}

interface InjuryRiskResponse {
  risk_score: number; tier: string; confidence: number;
  signal_contributions?: SignalContribution[];
  coach_actions: string[]; interaction_penalties: string[];
  data_quality: {
    signals_present: number; signals_total: number; shap_coverage: number;
    subjective_available: boolean; confidence_note: string;
  };
  regional_pain_summary?: RegionalPainSummary[];
  date: string; status?: string; message?: string;
  // New fields for network visualization
  risk_narrative?: string;
  signal_metadata?: Record<string, SignalMetadataEntry>;
  interaction_nodes?: InteractionNode[];
  // Contextual injury engine augmentation
  contextual_actions?: Array<{ action: string; basis: string[]; evidence: string }>;
  context_headline?: string;
  context_confidence_note?: string;
  injury_context_status?: 'available' | 'no_data' | 'insufficient_data' | 'unavailable';
  injury_context_onboarding?: { tier: string; message: string | null; detail: string | null };
  injury_context?: {
    healthy_baseline: any;
    correlation_patterns: any[];
    pre_injury_match_count: number;
    top_match_similarity: number;
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RISK_TIER_CONFIG: Record<string, { label: string; color: string }> = {
  LOW: { label: 'Low Risk', color: 'text-green-500' },
  MODERATE: { label: 'Moderate', color: 'text-yellow-500' },
  ELEVATED: { label: 'Elevated', color: 'text-orange-500' },
  HIGH: { label: 'High Risk', color: 'text-red-500' },
  CRITICAL: { label: 'Critical', color: 'text-red-800' },
};

const RISK_TIER_COLORS: Record<string, string> = {
  LOW: '#22c55e', MODERATE: '#eab308', ELEVATED: '#f97316',
  HIGH: '#ef4444', CRITICAL: '#991b1b',
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const AthleteStatusCard: React.FC<AthleteStatusCardProps> = ({ athleteId, isDark, defaultExpanded = false, connectedSources }) => {
  // Readiness state
  const [readinessData, setReadinessData] = useState<ReadinessResponse | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(true);
  const [readinessError, setReadinessError] = useState<string | null>(null);

  // Injury risk state
  const [riskData, setRiskData] = useState<InjuryRiskResponse | null>(null);
  const [timelineData, setTimelineData] = useState<InjuryRiskTimelinePoint[] | null>(null);
  const [riskLoading, setRiskLoading] = useState(true);

  // UI state
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState<ActiveTab>('readiness');

  // ---- Fetch combined athlete status (readiness + injury risk in one call) ----
  useEffect(() => {
    const run = async () => {
      try {
        setReadinessLoading(true);
        setRiskLoading(true);
        setReadinessError(null);

        const qs = 'include_details=true';
        const url = athleteId
          ? getApiUrl(`/api/athlete-status/${athleteId}/?${qs}`)
          : getApiUrl(`/api/athlete-status/?${qs}`);

        const result = await authenticatedFetchWithRetry<{
          readiness: ReadinessResponse | null;
          injury_risk: InjuryRiskResponse | null;
        }>(url, { method: 'GET' }, 'fetching athlete status', 2);

        if (!result.success) {
          setReadinessError(result.error || 'Failed to fetch athlete status');
          setReadinessLoading(false);
          setRiskLoading(false);
          return;
        }

        const data = result.data;

        // Handle top-level error status (backend returns 200 with status:"error" to avoid CloudFront masking)
        if ((data as any)?.status === 'error') {
          console.error('athlete-status returned error:', (data as any).error);
          setReadinessError((data as any).error || 'Failed to fetch athlete status');
          setReadinessLoading(false);
          setRiskLoading(false);
          return;
        }

        // Handle readiness
        const readiness = data?.readiness ?? null;
        if (readiness && ((readiness as any).status === 'no_data' || (readiness as any).status === 'error')) {
          setReadinessData(null);
        } else {
          setReadinessData(readiness);
        }

        // Handle injury risk
        const risk = data?.injury_risk ?? null;
        if (risk && ((risk as any).status === 'no_data' || (risk as any).status === 'insufficient_data' || (risk as any).status === 'error')) {
          setRiskData(null);
        } else {
          setRiskData(risk);
        }

        setReadinessLoading(false);
        setRiskLoading(false);

        // For self-view (no athleteId), trigger sync if readiness is stale
        if (!athleteId && readiness) {
          const today = new Date().toISOString().split('T')[0];
          if (readiness.date && readiness.date !== today) {
            const token = localStorage.getItem('access');
            if (token) {
              try {
                await fetch(getApiUrl('/api/wearables/sync/'), {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                });
                await new Promise(resolve => setTimeout(resolve, 6000));
                const refreshUrl = athleteId
                  ? getApiUrl(`/api/athlete-status/${athleteId}/?${qs}&refresh=true`)
                  : getApiUrl(`/api/athlete-status/?${qs}&refresh=true`);
                const refreshResult = await authenticatedFetchWithRetry<{
                  readiness: ReadinessResponse | null;
                  injury_risk: InjuryRiskResponse | null;
                }>(refreshUrl, { method: 'GET' }, 'refreshing athlete status', 2);
                if (refreshResult.success && refreshResult.data?.readiness) {
                  setReadinessData(refreshResult.data.readiness);
                }
                if (refreshResult.success && refreshResult.data?.injury_risk) {
                  setRiskData(refreshResult.data.injury_risk);
                }
              } catch { /* sync retry is best-effort */ }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching athlete status:', err);
        setReadinessError('Failed to fetch readiness score');
        setReadinessLoading(false);
        setRiskLoading(false);
      }
    };
    run();
  }, [athleteId]);

  // ---- Fetch timeline (after risk loads) ----
  useEffect(() => {
    if (!riskData || !athleteId) return;
    const run = async () => {
      try {
        const url = getApiUrl(`/api/injury-risk/${athleteId}/timeline/?days=45&days_timeline=28`);
        const result = await authenticatedFetchWithRetry<{ timeline: InjuryRiskTimelinePoint[] }>(
          url, { method: 'GET' }, 'fetching injury risk timeline', 1,
        );
        if (result.success && result.data?.timeline) setTimelineData(result.data.timeline);
      } catch { /* non-critical */ }
    };
    run();
  }, [riskData, athleteId]);

  // ---- Readiness helpers ----
  const getReadinessTier = (score: number) => {
    if (score >= 80) return { label: 'Optimal', color: 'text-green-500' };
    if (score >= 60) return { label: 'Moderate', color: 'text-yellow-400' };
    if (score >= 40) return { label: 'Poor', color: 'text-red-500' };
    return { label: 'Low', color: 'text-red-500' };
  };

  const getReadinessLabel = (score: number) => {
    if (score >= 80) return 'Ready to Train';
    if (score >= 65) return 'Train Smart';
    if (score >= 50) return 'Light Activity';
    return 'Recovery Priority';
  };

  const getMetrics = (): MetricData[] => {
    if (!readinessData?.components) return [];
    const metrics: MetricData[] = [];
    const c = readinessData.components;

    if (c.hrv && c.hrv.composite_score !== undefined) {
      const has = c.hrv.raw_value !== undefined && c.hrv.raw_value !== null && c.hrv.raw_value > 0;
      metrics.push({
        name: 'HRV', rawValue: has ? c.hrv.raw_value! : c.hrv.composite_score,
        unit: has ? (c.hrv.unit || 'ms') : '', score: c.hrv.composite_score,
        trend: c.hrv.trend ?? 0, weeklyTrend: c.hrv.weekly_trend ?? null,
        available: true, isEstimated: c.hrv.is_estimated ?? false,
      });
    }

    if (c.rhr !== undefined && c.rhr !== null) {
      if (typeof c.rhr === 'number') {
        metrics.push({ name: 'RHR', rawValue: c.rhr, unit: '', score: c.rhr, trend: 0, available: true });
      } else {
        const has = c.rhr.raw_value !== undefined && c.rhr.raw_value !== null && c.rhr.raw_value > 0;
        metrics.push({
          name: 'RHR', rawValue: has ? c.rhr.raw_value! : c.rhr.score,
          unit: has ? (c.rhr.unit || 'bpm') : '', score: c.rhr.score,
          trend: c.rhr.trend ?? 0, weeklyTrend: c.rhr.weekly_trend ?? null, available: true,
        });
      }
    }

    if (c.sleep && c.sleep.final_score !== undefined) {
      const has = c.sleep.raw_value !== undefined && c.sleep.raw_value !== null && c.sleep.raw_value > 0;
      metrics.push({
        name: 'Sleep', rawValue: has ? c.sleep.raw_value! : c.sleep.final_score,
        unit: has ? (c.sleep.unit || 'hrs') : '', score: c.sleep.final_score,
        trend: c.sleep.trend ?? 0, weeklyTrend: c.sleep.weekly_trend ?? null, available: true,
      });
    }

    if (c.acwr && c.acwr.score !== undefined) {
      const has = c.acwr.raw_value !== undefined && c.acwr.raw_value !== null;
      metrics.push({
        name: 'ACWR', rawValue: has ? c.acwr.raw_value! : c.acwr.score,
        unit: has ? 'ratio' : '', score: c.acwr.score, trend: 0, available: true,
      });
    }

    if (c.wellness && c.wellness.composite_score) {
      metrics.push({
        name: 'Wellness', rawValue: c.wellness.composite_score, unit: '',
        score: c.wellness.composite_score, trend: 0, available: true,
      });
    }

    return metrics;
  };

  const overallTrend = useMemo(() => {
    const metrics = getMetrics();
    const withValues = metrics.filter(m => m.trend !== 0);
    if (withValues.length === 0) return 0;
    return withValues.reduce((s, m) => s + m.trend, 0) / withValues.length;
  }, [readinessData]);

  const lowestMetric = useMemo(() => {
    const m = getMetrics();
    if (m.length === 0) return null;
    return m.reduce((low, cur) => cur.score < low.score ? cur : low);
  }, [readinessData]);

  const getActionableInsight = (metric: MetricData | null): string => {
    if (!metric) return '';
    switch (metric.name) {
      case 'HRV': return metric.score < 50 ? 'Focus on stress reduction and quality sleep tonight' : 'Consider light recovery activities today';
      case 'RHR': return metric.score < 50 ? 'Your resting heart rate indicates fatigue - prioritize rest' : 'Monitor hydration and avoid high-intensity training';
      case 'Sleep': return metric.score < 50 ? 'Prioritize 8+ hours of sleep and improve sleep hygiene' : 'Try going to bed 30 minutes earlier tonight';
      case 'ACWR': return metric.score < 50 ? 'Training load imbalance detected - reduce intensity' : 'Gradually increase training volume this week';
      case 'Wellness': return metric.score < 50 ? 'Focus on recovery: nutrition, hydration, and rest' : 'Check in on mood and stress levels';
      default: return 'Focus on recovery and monitor your metrics';
    }
  };

  // ---- Injury risk helpers ----
  const riskTierConfig = riskData ? (RISK_TIER_CONFIG[riskData.tier] || RISK_TIER_CONFIG.LOW) : null;
  const riskTierColor = riskData ? (RISK_TIER_COLORS[riskData.tier] || RISK_TIER_COLORS.LOW) : '#22c55e';
  const riskPercent = riskData ? Math.round(riskData.risk_score * 100) : 0;

  const topContributors = useMemo(() => {
    if (!riskData?.signal_contributions) return [];
    return riskData.signal_contributions
      .filter(c => c.direction === 'increasing_risk')
      .sort((a, b) => b.weighted_contribution - a.weighted_contribution)
      .slice(0, 7);
  }, [riskData]);

  // ---- Radar chart ----
  const getRadarChartData = (metrics: MetricData[]) => ({
    labels: metrics.map(m => m.name),
    datasets: [{
      label: 'Score',
      data: metrics.map(m => m.score),
      backgroundColor: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.12)',
      borderColor: isDark ? '#3B82F6' : '#2563EB',
      borderWidth: 2,
      pointBackgroundColor: isDark ? '#60A5FA' : '#3B82F6',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: isDark ? '#60A5FA' : '#3B82F6',
    }],
  });

  const radarOptions = useMemo(() => ({
    responsive: true, maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: { color: isDark ? '#374151' : '#9CA3AF' },
        grid: { color: isDark ? '#374151' : '#9CA3AF' },
        pointLabels: { color: isDark ? '#9CA3AF' : '#1F2937', font: { size: 12 } },
        ticks: { color: isDark ? '#6B7280' : '#374151', backdropColor: 'transparent', font: { size: 10 }, stepSize: 20 },
        suggestedMin: 0, suggestedMax: 100,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
        titleColor: isDark ? '#F9FAFB' : '#111827',
        bodyColor: isDark ? '#D1D5DB' : '#374151',
        borderColor: isDark ? '#374151' : '#E5E7EB',
        borderWidth: 1, cornerRadius: 8,
        callbacks: { label: (ctx: any) => ` ${ctx.label}: ${ctx.raw.toFixed(1)}` },
      },
    },
  }), [isDark]);

  // ---- Loading ----
  if (readinessLoading && riskLoading) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-3" />
            <p className="cgm-text-secondary text-sm">Loading athlete status...</p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Empty state ----
  if (!readinessData && !readinessLoading && !readinessError && !riskData) {
    return (
      <div className="cgm-metric-card rounded-2xl p-8">
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-500/10">
            <Watch className="w-10 h-10 text-blue-500" />
          </div>
          <div className="space-y-2">
            <h3 className="text-2xl font-bold">No Data Yet</h3>
            <p className="theme-text-secondary text-base max-w-md mx-auto leading-relaxed">
              Connect your Whoop or Garmin device to start tracking readiness and injury risk.
            </p>
          </div>
          <Link
            to="/connect-wearable"
            className="inline-flex items-center space-x-2 cgm-button px-8 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
          >
            <Watch className="w-5 h-5" />
            <span>Connect Device</span>
          </Link>
        </div>
      </div>
    );
  }

  // ---- Error state ----
  if (readinessError && !riskData) {
    return (
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-center space-y-4">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <div>
              <p className="text-red-500 text-sm font-medium mb-2">{readinessError}</p>
              <Link to="/connect-wearable" className="text-blue-500 hover:text-blue-600 text-sm font-medium underline">
                Check device connection
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const metrics = getMetrics();
  const readinessTier = readinessData ? getReadinessTier(readinessData.readiness_score) : null;

  return (
    <div
      className={`cgm-metric-card rounded-2xl p-6 transition-all duration-300 ease-in-out border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}
    >
      {/* ================================================================ */}
      {/* COLLAPSED HEADER - always visible                                */}
      {/* ================================================================ */}
      <div
        className="flex items-center justify-between mb-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        data-tour="athlete-status-expand-toggle"
        aria-expanded={isExpanded}
      >
        {/* Left: Title + badges */}
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-lg font-bold cgm-text-primary whitespace-nowrap">Athlete Status</h3>

          {/* Readiness badge */}
          {readinessTier && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap ${readinessTier.color}`}
              style={{ backgroundColor: readinessTier.color.includes('green') ? '#22c55e15' : readinessTier.color.includes('yellow') ? '#eab30815' : '#ef444415' }}
            >
              {readinessTier.label}
            </span>
          )}

          {/* Injury risk badge */}
          {riskTierConfig && riskData && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 whitespace-nowrap ${riskTierConfig.color}`}
              style={{ backgroundColor: `${riskTierColor}15` }}
            >
              <Shield className="w-3 h-3 flex-shrink-0" />
              {riskTierConfig.label}
            </span>
          )}
        </div>

        {/* Right: Confidence + chevron */}
        <div className="flex items-center gap-3">
          {riskData && (
            <div className="hidden sm:flex flex-col items-end gap-0.5">
              <div className="text-[11px] font-medium cgm-text-secondary flex items-center gap-0.5">
                Baseline Confidence
                <InfoTooltip
                  title={INJURY_RISK_CONFIDENCE_TOOLTIP.title}
                  body={INJURY_RISK_CONFIDENCE_TOOLTIP.body}
                  isDark={isDark}
                  width="w-72"
                  iconSize={2.5}
                  align="right"
                  ariaLabel="How Baseline Confidence is calculated"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`w-16 h-1.5 rounded-full ${isDark ? 'bg-neutral-700' : 'bg-gray-200'} overflow-hidden`}>
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{ width: `${Math.round(riskData.confidence * 100)}%`, backgroundColor: riskData.confidence >= 0.7 ? '#3b82f6' : '#f59e0b' }}
                  />
                </div>
                <span className="text-xs font-semibold cgm-text-primary">{Math.round(riskData.confidence * 100)}%</span>
              </div>
            </div>
          )}
          <button
            className="text-gray-400 hover:text-gray-200 transition-colors p-1"
            onClick={(e) => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/* ALWAYS-VISIBLE: Score + Coach Action                             */}
      {/* ================================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        {/* Readiness score */}
        {readinessData && (
          <div>
            <div className="text-sm cgm-text-secondary mb-1">Readiness</div>
            <div className="flex items-baseline gap-2">
              <div className="text-4xl font-bold cgm-text-primary">{readinessData.readiness_score.toFixed(0)}%</div>
            </div>
            <div className="text-xs cgm-text-secondary mt-1">{getReadinessLabel(readinessData.readiness_score)}</div>
          </div>
        )}

        {/* Injury risk score */}
        {riskData && (
          <div>
            <div className="text-sm cgm-text-secondary mb-1">Injury Risk</div>
            <div className="flex items-baseline gap-2">
              <div className="text-4xl font-bold" style={{ color: riskTierColor }}>{riskPercent}</div>
              <span className={`text-xs font-semibold ${riskTierConfig!.color}`}>{riskTierConfig!.label.toUpperCase()}</span>
            </div>
            {riskData.coach_actions.length > 0 && (
              <div className={`flex items-start gap-2 p-2 mt-2 rounded-lg text-sm ${isDark ? 'bg-neutral-800/50' : 'bg-gray-50'}`}>
                <Activity className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: riskTierColor }} />
                <span className="text-xs cgm-text-primary">{riskData.coach_actions[0]}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lowest metric warning */}
      {lowestMetric && lowestMetric.score < 70 && (
        <div className={`flex items-start gap-3 p-3 rounded-xl mb-4 ${isDark ? 'bg-amber-900/20 border-amber-800/30' : 'bg-amber-50 border-amber-200'} border`}>
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
              Watch: {lowestMetric.name} {lowestMetric.unit ? `(${lowestMetric.rawValue.toFixed(1)}${lowestMetric.unit})` : `(Score: ${lowestMetric.score.toFixed(0)})`}
            </span>
            <span className={`text-xs ${isDark ? 'text-amber-200/80' : 'text-amber-700'}`}>
              {getActionableInsight(lowestMetric)}
            </span>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* EXPANDED CONTENT - Tabs                                          */}
      {/* ================================================================ */}
      <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[5000px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`}>
        {/* Tab bar */}
        <div className={`flex gap-1 mb-5 p-1 rounded-lg ${isDark ? 'bg-neutral-800/50' : 'bg-gray-100'}`}>
          <TabButton label="Performance Readiness" icon={<TrendingUp className="w-3.5 h-3.5" />}
            active={activeTab === 'readiness'} isDark={isDark}
            onClick={(e) => { e.stopPropagation(); setActiveTab('readiness'); }} />
          <TabButton
            label="Injury Risk" icon={<Shield className="w-3.5 h-3.5" />}
            active={activeTab === 'injury-risk'} isDark={isDark}
            onClick={(e) => { e.stopPropagation(); setActiveTab('injury-risk'); }}
            badge={riskData && riskData.tier !== 'LOW' ? riskTierConfig?.label : undefined}
            badgeColor={riskTierColor}
            dataTour="athlete-status-injury-tab"
          />
        </div>

        {/* Tab content — readiness tab keeps a tab-level export wrapper
            (single radar chart). Injury risk tab does NOT wrap at tab level
            because it has multiple distinct sections with their own scoped
            export controls (signal flow, top contributors, etc.). */}
        {activeTab === 'readiness' ? (
          <OverlayExportWrapper metricName="readiness" showStyleToggle={false}>
            <ReadinessTabContent
              readinessData={readinessData}
              metrics={metrics}
              isDark={isDark}
              radarChartData={metrics.length > 0 ? getRadarChartData(metrics) : null}
              radarOptions={radarOptions}
            />
          </OverlayExportWrapper>
        ) : (
          <InjuryRiskTabContent
            riskData={riskData}
            timelineData={timelineData}
            topContributors={topContributors}
            isDark={isDark}
            athleteId={athleteId}
            connectedSources={connectedSources}
          />
        )}
      </div>

      {/* Warnings */}
      {readinessData?.warnings && readinessData.warnings.length > 0 && (
        <div className={`mt-4 p-4 ${isDark ? 'bg-amber-950/40 border-amber-700/50' : 'bg-amber-50 border-amber-300'} border`}>
          <p className={`text-sm font-semibold ${isDark ? 'text-amber-400' : 'text-amber-800'} mb-2 flex items-center gap-2`}>
            <AlertCircle className="w-4 h-4" />Data Warnings
          </p>
          <ul className={`text-sm ${isDark ? 'text-amber-200' : 'text-amber-700'} space-y-1 ml-6`}>
            {readinessData.warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={`${isDark ? 'text-amber-400' : 'text-amber-600'} mt-0.5`}>&#8226;</span>
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-gray-200/50 dark:border-gray-700/50 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <p className="text-xs cgm-text-secondary text-center">
          Updated: {new Date(readinessData?.date || riskData?.date || Date.now()).toLocaleDateString()} |
          {isExpanded ? ' Click to collapse' : ' Click to expand details'}
        </p>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Tab Button
// ---------------------------------------------------------------------------

interface TabButtonProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  isDark: boolean;
  onClick: (e: React.MouseEvent) => void;
  badge?: string;
  badgeColor?: string;
  /** Optional `data-tour` anchor slug for guided tutorials. */
  dataTour?: string;
}

const TabButton: React.FC<TabButtonProps> = ({ label, icon, active, isDark, onClick, badge, badgeColor, dataTour }) => (
  <button
    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-200 ${
      active
        ? (isDark ? 'bg-neutral-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
        : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
    }`}
    onClick={onClick}
    data-tour={dataTour}
  >
    {icon}
    <span>{label}</span>
    {badge && (
      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${badgeColor}20`, color: badgeColor }}>
        {badge}
      </span>
    )}
  </button>
);


// ---------------------------------------------------------------------------
// Readiness Tab Content
// ---------------------------------------------------------------------------

interface ReadinessTabContentProps {
  readinessData: ReadinessResponse | null;
  metrics: MetricData[];
  isDark: boolean;
  radarChartData: any;
  radarOptions: any;
}

const ReadinessTabContent: React.FC<ReadinessTabContentProps> = ({ readinessData, metrics, isDark, radarChartData, radarOptions }) => {
  if (!readinessData) {
    return <p className="text-sm cgm-text-secondary text-center py-4">No readiness data available.</p>;
  }

  return (
    <div>
      {/* Radar chart (large) */}
      {radarChartData && metrics.length > 0 && (
        <div
          className="p-4 border mb-4"
          style={{ background: 'var(--bg-card)', borderColor: 'var(--card-border)' }}
        >
          <h4 className="text-sm font-semibold cgm-text-secondary mb-3 flex items-center gap-1.5">
            Readiness Breakdown
            <InfoTooltip title={READINESS_SCORE_TOOLTIP.title} body={READINESS_SCORE_TOOLTIP.body} isDark={isDark} iconSize={3} ariaLabel="How Readiness Score is calculated" />
          </h4>
          <div className="h-56">
            <Radar data={radarChartData} options={radarOptions} />
          </div>
        </div>
      )}

      {/* Component details */}
      {metrics.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold cgm-text-secondary mb-3">Component Details</h4>
          <div className="space-y-2">
            {metrics.map((metric) => {
              const hasRaw = metric.unit && metric.unit.length > 0;
              const displayValue = hasRaw
                ? (metric.name === 'ACWR' ? metric.rawValue.toFixed(2) : metric.rawValue.toFixed(metric.unit === 'bpm' ? 0 : 1))
                : metric.rawValue.toFixed(1);
              const displayUnit = hasRaw ? metric.unit : '';
              const tooltip = COMPONENT_TOOLTIPS[metric.name];

              return (
                <div key={metric.name}
                  className="flex items-center justify-between p-3 border"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--card-border)' }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs cgm-text-secondary tracking-wide flex items-center gap-1.5">
                      {metric.name}
                      {metric.isEstimated && <span className="text-amber-500 cursor-help" title="Estimated from heart rate data">~</span>}
                      {tooltip && <InfoTooltip title={tooltip.title} body={tooltip.body} isDark={isDark} ariaLabel={`How ${metric.name} is calculated`} />}
                    </span>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className={`text-lg font-bold ${metric.isEstimated ? (isDark ? 'text-amber-400/80' : 'text-amber-600/80') : (isDark ? 'text-white' : 'text-gray-900')}`}>
                        {displayValue}
                      </span>
                      {displayUnit && <span className="text-xs cgm-text-secondary">{displayUnit}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{metric.score.toFixed(0)}/100</span>
                    {metric.trend !== 0 ? (
                      <div className={`flex items-center gap-0.5 ${metric.trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {metric.trend > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        <span className="text-xs font-medium">{metric.trend > 0 ? '+' : ''}{metric.trend.toFixed(1)}%</span>
                      </div>
                    ) : <Minus className="w-3.5 h-3.5 text-gray-400" />}
                    {metric.weeklyTrend != null && metric.weeklyTrend !== 0 && (
                      <div className={`flex items-center gap-0.5 ${metric.weeklyTrend > 0 ? 'text-green-500/70' : 'text-red-500/70'}`}
                        title={`7-day trend: ${metric.weeklyTrend > 0 ? '+' : ''}${metric.weeklyTrend.toFixed(1)}%`}
                      >
                        <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>7d</span>
                        {metric.weeklyTrend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Device attribution */}
      {readinessData.device_confidence && (
        <div className="mt-3 pt-3 border-t border-gray-200/50 dark:border-gray-700/50 flex justify-end">
          <div className="text-xs cgm-text-secondary">
            Data powered by <span className="font-semibold capitalize">{readinessData.device_confidence.device_type === 'whoop' ? 'WHOOP' : readinessData.device_confidence.device_type}</span>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Contextual Recommendations (expandable evidence)
// ---------------------------------------------------------------------------

interface ContextualRecommendationsProps {
  actions: Array<{ action: string; basis: string[]; evidence: string }>;
  isDark: boolean;
}

const ContextualRecommendations: React.FC<ContextualRecommendationsProps> = ({ actions, isDark }) => {
  const [expandedIdx, setExpandedIdx] = React.useState<number | null>(null);

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        fontSize: '10px',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
        letterSpacing: '0.1em',
        fontWeight: 600,
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}>
        Recommendations
        <InfoTooltip title={CONTEXTUAL_RECOMMENDATIONS_TOOLTIP.title} body={CONTEXTUAL_RECOMMENDATIONS_TOOLTIP.body} isDark={isDark} iconSize={3} ariaLabel="How recommendations are generated" />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {actions.map((item, i) => {
          const isExpanded = expandedIdx === i;
          return (
            <div
              key={i}
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--card-border)',
                overflow: 'hidden',
              }}
            >
              <button
                onClick={() => setExpandedIdx(isExpanded ? null : i)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  color: 'var(--text-muted)',
                  flexShrink: 0,
                }}>
                  {isExpanded ? '\u25BE' : '\u25B8'}
                </span>
                <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                  {item.action}
                </span>
              </button>
              {isExpanded && (
                <div style={{
                  padding: '0 14px 12px 32px',
                  borderTop: '1px solid var(--card-border)',
                }}>
                  <p style={{
                    fontSize: '11px',
                    color: 'var(--text-secondary)',
                    lineHeight: 1.6,
                    margin: '10px 0 0 0',
                  }}>
                    {item.evidence}
                  </p>
                  {item.basis.length > 0 && (
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      flexWrap: 'wrap',
                      marginTop: '8px',
                    }}>
                      {item.basis.map((key) => (
                        <span
                          key={key}
                          style={{
                            fontSize: '10px',
                            fontFamily: 'monospace',
                            padding: '2px 6px',
                            border: '1px solid var(--card-border)',
                            color: 'var(--text-muted)',
                          }}
                        >
                          {key.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Injury Risk Tab Content
// ---------------------------------------------------------------------------

interface InjuryRiskTabContentProps {
  riskData: InjuryRiskResponse | null;
  timelineData: InjuryRiskTimelinePoint[] | null;
  topContributors: SignalContribution[];
  isDark: boolean;
  athleteId?: number;
  connectedSources?: string[];
}

const InjuryRiskTabContent: React.FC<InjuryRiskTabContentProps> = ({ riskData, timelineData, topContributors, isDark, athleteId, connectedSources }) => {
  if (!riskData) {
    return <p className="text-sm cgm-text-secondary text-center py-4">No injury risk data available.</p>;
  }

  const allContributions = riskData.signal_contributions || [];
  const signalMeta = riskData.signal_metadata || {};
  const rawNodes = riskData.interaction_nodes;
  const interactionNodes: InteractionNode[] = Array.isArray(rawNodes)
    ? rawNodes
    : rawNodes && typeof rawNodes === 'object'
      ? Object.entries(rawNodes).map(([id, v]: [string, any]) => ({ id, ...v }))
      : [];
  const topThree = topContributors.slice(0, 3);
  const previousRiskScore = timelineData && timelineData.length > 5
    ? timelineData[timelineData.length - 6]?.risk_score
    : undefined;

  return (
    <div>
      {/* Signal Flow Network */}
      {allContributions.length > 0 && Object.keys(signalMeta).length > 0 && (
        <OverlayExportWrapper metricName="signal_flow" showStyleToggle={false} className="mb-4">
          <SignalFlowNetwork
            riskScore={riskData.risk_score}
            tier={riskData.tier}
            previousRiskScore={previousRiskScore}
            contributions={allContributions}
            interactionPenalties={riskData.interaction_penalties}
            signalMetadata={signalMeta}
            interactionNodes={interactionNodes}
            isDark={isDark}
            connectedSources={connectedSources}
          />
        </OverlayExportWrapper>
      )}

      {/* Risk Narrative */}
      {(riskData.risk_narrative || riskData.context_headline) && (
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--card-border)',
          padding: '16px 20px',
          marginBottom: '20px',
        }}>
          {riskData.context_headline && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '6px',
              marginBottom: riskData.risk_narrative ? '8px' : 0,
            }}>
              <p style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                lineHeight: 1.65,
                margin: 0,
                flex: 1,
              }}>
                {riskData.context_headline}
              </p>
              <InfoTooltip title={CONTEXT_HEADLINE_TOOLTIP.title} body={CONTEXT_HEADLINE_TOOLTIP.body} isDark={isDark} iconSize={3} ariaLabel="About historical context analysis" align="right" />
            </div>
          )}
          {riskData.risk_narrative && (
            <p style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              lineHeight: 1.65,
              margin: 0,
            }}>
              {riskData.risk_narrative}
            </p>
          )}
          <div style={{
            fontSize: '10px',
            fontFamily: 'monospace',
            color: 'var(--text-muted)',
            marginTop: '10px',
          }}>
            {allContributions.length} signals | {Math.round(riskData.confidence * 100)}% confidence | {riskData.date}
            {riskData.context_confidence_note && ` | ${riskData.context_confidence_note}`}
          </div>
        </div>
      )}

      {/* Top 3 Signal Cards */}
      {topThree.length > 0 && (
        <div style={{ marginBottom: '20px' }} data-tour="athlete-signal-contributors">
          <div style={{
            fontSize: '10px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            letterSpacing: '0.1em',
            fontWeight: 600,
            marginBottom: '8px',
          }}>
            Top Contributors
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '12px',
          }}>
            {topThree.map((contrib) => (
              <SignalContributorCard
                key={contrib.signal}
                contribution={contrib}
                timelineData={timelineData}
                metadata={signalMeta[contrib.signal]}
                isDark={isDark}
              />
            ))}
          </div>
        </div>
      )}

      {/* Cross-Signal Amplifier callout */}
      {riskData.interaction_penalties.length > 0 && (
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--card-border)',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          flexDirection: 'row',
          gap: '14px',
          alignItems: 'flex-start',
        }}>
          {/* Icon */}
          <div style={{
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(239,68,68,0.3)',
            flexShrink: 0,
            fontFamily: 'monospace',
            fontSize: '14px',
            color: '#ef4444',
          }}>
            &times;
          </div>

          <div>
            <div style={{
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: 'var(--text-secondary)',
              marginBottom: '6px',
              fontWeight: 600,
            }}>
              Cross-Signal Amplifier(s)
            </div>
            {riskData.interaction_penalties.map((penalty, i) => (
              <p key={i} style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: i === 0 ? 0 : '4px 0 0 0',
              }}>
                {penalty}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Coach Recommendations — contextual actions with evidence, or fallback to rule-based */}
      {(riskData.contextual_actions && riskData.contextual_actions.length > 0) ? (
        <ContextualRecommendations actions={riskData.contextual_actions} isDark={isDark} />
      ) : riskData.coach_actions.length > 1 ? (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '10px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            letterSpacing: '0.1em',
            fontWeight: 600,
            marginBottom: '8px',
          }}>
            Recommendations
          </div>
          <ul style={{ margin: 0, paddingLeft: '16px' }}>
            {riskData.coach_actions.slice(1).map((action, i) => (
              <li key={i} style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                {action}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Compact Timeline */}
      {timelineData && timelineData.length > 3 && (
        <CompactTimeline data={timelineData} isDark={isDark} />
      )}

      {/* Injury History */}
      {athleteId && (
        <InjuryHistoryPanel
          athleteId={athleteId}
          isDark={isDark}
          currentPainByRegion={
            riskData.regional_pain_summary
              ? Object.fromEntries(riskData.regional_pain_summary.map((rp: any) => [rp.region, rp.pain_level]))
              : {}
          }
        />
      )}

      {/* Context engine onboarding nudge */}
      {riskData.injury_context_onboarding && riskData.injury_context_onboarding.message && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          padding: '12px 16px',
          marginBottom: '20px',
          background: 'var(--bg-card)',
          border: '1px solid var(--card-border)',
        }}>
          <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>{'\u25CE'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                {riskData.injury_context_onboarding.message}
              </span>
              <InfoTooltip title={INJURY_CONTEXT_ONBOARDING_TOOLTIP.title} body={INJURY_CONTEXT_ONBOARDING_TOOLTIP.body} isDark={isDark} iconSize={3} ariaLabel="About pattern analysis" />
            </div>
            {riskData.injury_context_onboarding.detail && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', lineHeight: 1.5 }}>
                {riskData.injury_context_onboarding.detail}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Quality */}
      <div className={`flex items-center justify-between text-xs cgm-text-secondary p-2 ${isDark ? 'bg-neutral-800/50' : 'bg-gray-50'}`}>
        <span>{riskData.data_quality.signals_present}/{riskData.data_quality.signals_total} signals</span>
        <span>{riskData.data_quality.subjective_available ? 'Subjective data available' : 'Wearable data only'}</span>
        <span>{riskData.data_quality.confidence_note}</span>
      </div>
    </div>
  );
};



export default AthleteStatusCard;
